<template>
  <div class="attended-presentation">
    <div class="container">
      <h3 class="attended-presentation-count">
        {{ $t('attended') }} ({{ total }})
      </h3>
      <AttendedPresentationList
        v-if="total > 0"
        :list="attendedPresentationList"
      />
      <p v-if="isShowTextNote" class="text-note">
        {{ $t('your_future_presentations_shown_here') }}
      </p>
      <div
        v-if="isLoadingAttendedPresentations"
        class="attended-presentation-loading"
      >
        <SkeletonLoadingBars v-for="index in 1" :key="index" />
      </div>
      <EmptyList v-if="isShowEmptyList" />
      <div ref="sentinel" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import AttendedPresentationsApi from '@/api/attended-presentations'
import SkeletonLoadingBars from '@/components/my-quiz-answers/SkeletonLoadingBars.vue'
import AttendedPresentationList from './AttendedPresentationList.vue'
import EmptyList from './EmptyList.vue'

export default {
  components: {
    AttendedPresentationList,
    EmptyList,
    SkeletonLoadingBars,
  },
  data() {
    return {
      observer: null,
      page: 1,
      pageSize: 10,
      total: 0,
      attendedPresentationList: [],
      isLoadingAttendedPresentations: false,
      isFullyLoadedData: false,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
    }),
    isShowEmptyList() {
      return !this.isLoadingAttendedPresentations && this.total === 0
    },
    isShowTextNote() {
      return !this.isLoadingAttendedPresentations
        && this.total > 0
        && this.total < 3
    },
  },
  mounted() {
    this.initInfinityScroll()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    async getAttendedPresentations() {
      if (this.isFullyLoadedData || this.isLoadingAttendedPresentations) return

      this.isLoadingAttendedPresentations = true

      try {
        const resp = await AttendedPresentationsApi.getAttendedPresentationList({
          page: this.page,
          pageSize: this.pageSize,
        })
        const { data, total } = resp
        this.total = total
        const transformData = this._transformData(data)
        this.attendedPresentationList = [
          ...this.attendedPresentationList,
          ...transformData,
        ]

        if (this.attendedPresentationList.length >= this.total) {
          this.isFullyLoadedData = true
        } else {
          this.page += 1
        }
      } finally {
        this.isLoadingAttendedPresentations = false
      }
    },
    _transformData(data) {
      return data.map((item) => ({
        id: item.id,
        userId: item.userId,
        presentationId: item.presentationId,
        name: item.presentation && item.presentation.name,
        joinAt: item.joinAt,
        thumbnailImage: item.presentation && item.presentation.thumbnailImage,
        customThumbnailImage: item.presentation && item.presentation.customThumbnailImage,
      }))
    },
    initInfinityScroll() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
      const target = this.$refs.sentinel
      const callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.getAttendedPresentations()
          }
        })
      }

      this.observer = new IntersectionObserver(callback, options)
      this.observer.observe(target)
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  margin: 0 auto;
  max-width: 600px;
}

.attended-presentation-count {
  margin-top: 8px;
  margin-bottom: 16px;
}

.text-note {
  padding: 0 16px;
  color: #707070;
  text-align: center;
  font-size: 0.875rem;
}
</style>
